import React, { useState } from "react";
import {
  Button,
  Card,
  Table,
  Modal,
  Form,
  Select,
  Input,
  message,
  Tag,
} from "antd";
import { SwapOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchAvailableVehicles_api,
  TransferVehicle_api,
} from "../../Api/Stock/stock_api";
import { FetchDealers_api } from "../../Api/Dealers/dealer_api";
import { useAuthChecker } from "../../Context";

const { Option } = Select;

export const ManageStockTransfer = () => {
  const { user } = useAuthChecker();
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [form] = Form.useForm();

  // Pagination & Search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [filterEngine, setFilterEngine] = useState("");
  const [filterChassis, setFilterChassis] = useState("");
  const [filterDealer, setFilterDealer] = useState<number | undefined>(
    undefined,
  );

  const {
    data: vehiclesData,
    refetch: refetchVehicles,
    isFetching,
  } = useQuery({
    queryKey: [
      "available_vehicles",
      page,
      pageSize,
      searchText,
      filterEngine,
      filterChassis,
      filterDealer,
    ],
    queryFn: () =>
      FetchAvailableVehicles_api(page, pageSize, {
        search: searchText,
        engine_no: filterEngine,
        chassis_no: filterChassis,
        dealer_id: filterDealer,
      }),
  });

  const { data: dealersData } = useQuery({
    queryKey: ["dealers", "page", "pageSize", "searchText"],
    queryFn: () => FetchDealers_api(page, pageSize, searchText),
  });

  const handleTransferClick = (record: any) => {
    setSelectedVehicle(record);
    form.resetFields();
    setModalOpen(true);
  };

  const handleTransferSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: selectedVehicle.id,
        to_dealer_id: values.to_dealer_id,
        remarks: values.remarks,
        userId: user?.userId,
      };
      const res = await TransferVehicle_api(payload);
      if (res.isTransferred) {
        message.success(res.msg);
        setModalOpen(false);
        refetchVehicles();
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "ID", dataIndex: "id", key: "id", width: 60 },
    {
      title: "Vehicle",
      key: "vehicle",
      render: (record: any) => (
        <div>
          <div className="font-bold">
            {record.v_type} - {record.v_modal}
          </div>
          <div className="text-xs text-gray-500">{record.color_name}</div>
        </div>
      ),
    },
    { title: "Engine No", dataIndex: "engine_number", key: "engine_number" },
    { title: "Chassis No", dataIndex: "chassis_number", key: "chassis_number" },
    {
      title: "Dealer",
      key: "dealer_name",
      dataIndex: "dealer_name",
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Button
          type="primary"
          size="small"
          icon={<SwapOutlined />}
          onClick={() => handleTransferClick(record)}
        >
          Transfer
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Stock Transfer
          </h1>
          <p className="text-gray-500">
            Transfer vehicles from Main/David stock to Dealers
          </p>
        </div>
      </div>

      <Card className="shadow-sm rounded-xl border-none mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Search All
            </p>
            <Input
              placeholder="Type Type, Model, Color..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Engine No
            </p>
            <Input
              placeholder="Filter Engine Number..."
              value={filterEngine}
              onChange={(e) => {
                setFilterEngine(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Chassis No
            </p>
            <Input
              placeholder="Filter Chassis Number..."
              value={filterChassis}
              onChange={(e) => {
                setFilterChassis(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Dealer (Location)
            </p>
            <Select
              placeholder="Filter by Dealer..."
              className="w-full"
              showSearch
              allowClear
              optionFilterProp="children"
              value={filterDealer}
              onChange={(val) => {
                setFilterDealer(val);
                setPage(1);
              }}
            >
              {dealersData?.dealerData?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      <Card className="shadow-sm rounded-xl border-none">
        <Table
          loading={isFetching}
          dataSource={vehiclesData?.data || []}
          columns={columns}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: vehiclesData?.total || 0,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
          className="overflow-x-auto"
        />
      </Card>

      <Modal
        title="Transfer Vehicle"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div className="mb-4 p-3 bg-blue-50 rounded text-blue-800 text-sm">
          Transferring{" "}
          <strong>
            {selectedVehicle?.v_type} {selectedVehicle?.v_modal}
          </strong>{" "}
          ({selectedVehicle?.engine_number})
        </div>
        <Form form={form} layout="vertical" onFinish={handleTransferSubmit}>
          <Form.Item
            label="Select Dealer"
            name="to_dealer_id"
            rules={[{ required: true, message: "Please select a dealer" }]}
          >
            <Select
              placeholder="Select Dealer"
              showSearch
              optionFilterProp="children"
            >
              {dealersData?.dealerData?.map((d: any) => (
                <Option key={d.id} value={d.id}>
                  {d.name} ({d.contact_number})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea
              rows={3}
              placeholder="Any notes about this transfer..."
            />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Confirm Transfer
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
